import React, { useEffect, useRef } from 'react';
import { Bot, Loader, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { useTheme } from '../../ThemeContext.tsx';
import { sanitizeMarkdown } from '../../utils/chatUtils.ts';
import {
    extractPlainText,
    findComparisonNumbers,
    colorizeReactChildrenByRange,
} from '../../utils/markdownComparisonColorize.tsx';
import { Message } from '../../types/chat.ts';
import { DataDisplay } from '../DataDisplay.tsx';
import MessageFeedback from '../Feedback.tsx';
import LoadingProgress from './LoadingProgress.tsx';

type ChatLogProps = {
    messages: Message[];
    chatAreaRef: React.RefObject<HTMLDivElement>;
    onMessageUpdate?: (messageId: string, updates: Partial<Message>) => void;
    sessionId?: string | null;
    onChartRequest?: (query: string) => void;
};

/**
 * Get the context text for sharing a chart.
 * This collects the bot's detailed response from the original query,
 * before the "create chart" request, combined with the current chart summary.
 */
const getShareContextText = (messages: Message[], currentIndex: number): string => {
    const currentMsg = messages[currentIndex];
    if (!currentMsg || currentMsg.sender !== 'bot') return currentMsg?.text || '';

    const contextParts: string[] = [];

    // Look backwards from the current message to find the detailed context
    // Typical flow: 
    //   1. User asks question
    //   2. Bot gives detailed response (possibly with table) <- WE WANT THIS
    //   3. User says "create chart"
    //   4. Bot shows chart with brief summary <- CURRENT MESSAGE

    // First, find the "create chart" user message
    let createChartIndex = -1;
    for (let i = currentIndex - 1; i >= 0 && i >= currentIndex - 3; i--) {
        const msg = messages[i];
        if (msg.sender === 'user') {
            const text = msg.text.toLowerCase();
            if (text.includes('chart') || text.includes('graph') || text.includes('visuali')) {
                createChartIndex = i;
                break;
            }
        }
    }

    // Now look for bot messages before the "create chart" request
    // These contain the detailed analysis we want
    const searchStart = createChartIndex > 0 ? createChartIndex - 1 : currentIndex - 2;

    for (let i = searchStart; i >= 0 && i >= searchStart - 6; i--) {
        const msg = messages[i];

        // Found a bot message with substantial text (the detailed response)
        if (msg.sender === 'bot' && msg.text && !msg.isLoading && !msg.error) {
            // Skip if this message has chart data (it's another chart response)
            const hasChartData = msg.data?.charts && msg.data.charts.length > 0;
            if (hasChartData) continue;

            // This is likely the detailed response - add it
            if (msg.text.length > 50) { // Only include if it's substantial
                contextParts.unshift(msg.text);
                break; // Found the detailed response
            }
        }

        // If we hit another user message (the original question), stop looking
        if (msg.sender === 'user' && i < searchStart) {
            break;
        }
    }

    // Add current message text (the chart summary)
    if (currentMsg.text) {
        contextParts.push(currentMsg.text);
    }

    return contextParts.join('\n\n');
};

const buildMarkdownComponents = (deltaColors: { positive: string; negative: string } | null) => {
    const applyComparisonColoring = (children: any) => {
        if (!deltaColors) return children;
        const plain = extractPlainText(children);
        const matches = findComparisonNumbers(plain);
        if (matches.length === 0) return children;

        let result = children;
        for (const match of matches) {
            const className = match.polarity === 'positive' ? deltaColors.positive : deltaColors.negative;
            // Since `colorizeReactChildrenByRange` assigns keys like "cmp_0", we should just let it run.
            // React handles fragments / nested arrays fine without custom keys unless we encounter a strict mode warning.
            // To be perfectly safe, we can use it sequentially.
            result = colorizeReactChildrenByRange(result, { start: match.start, end: match.end }, className);
        }
        return result;
    };

    return {
        h1: ({ node, ...props }: any) => (
            <h1 className="mt-4 mb-2 text-xl font-semibold" {...props} />
        ),
        h2: ({ node, ...props }: any) => (
            <h2 className="mt-4 mb-2 text-lg font-semibold" {...props} />
        ),
        h3: ({ node, ...props }: any) => (
            <h3 className="mt-3 mb-1 text-base font-semibold" {...props} />
        ),
        strong: ({ node, ...props }: any) => (
            <strong className="font-semibold" {...props} />
        ),
        p: ({ node, children, ...props }: any) => (
            <p {...props}>{applyComparisonColoring(children)}</p>
        ),
        li: ({ node, children, ...props }: any) => (
            <li {...props}>{applyComparisonColoring(children)}</li>
        ),
        hr: () => <hr className="my-3 border-t border-gray-200" />,
        div: ({ node, ...props }: any) => (
            <div className="prose dark:prose-invert max-w-none break-words" {...props} />
        ),
    };
};

const ChatLog: React.FC<ChatLogProps> = ({ messages, chatAreaRef, onMessageUpdate, sessionId, onChartRequest, }) => {
    const { colors, isMobile } = useTheme();
    const prevMessageCountRef = useRef(messages.length);

    const botMarkdownComponents = React.useMemo(() => {
        // Heineken palette: green/red for deltas. Keep `font-semibold` so the number pops similarly to bold markdown.
        return buildMarkdownComponents({
            positive: 'text-[#13670B] bg-[#13670B]/10 font-semibold px-1 rounded',
            negative: 'text-[#FF2B00] bg-[#FF2B00]/10 font-semibold px-1 rounded',
        });
    }, []);

    const userMarkdownComponents = React.useMemo(() => {
        return buildMarkdownComponents(null);
    }, []);

    useEffect(() => {
        // Only scroll if a new message was added (length increased)
        if (messages.length > prevMessageCountRef.current) {
            if (chatAreaRef.current) {
                chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
            }
        }
        // Update the ref for next comparison
        prevMessageCountRef.current = messages.length;
    }, [messages]);

    return (
        <div
            ref={chatAreaRef}
            className={`space-y-6 overflow-y-auto hide-scrollbar ${isMobile ? "max-w-full px-4" : "max-w-5xl mx-auto"}`}
            style={{ maxHeight: "calc(100vh - 180px)" }}
        >
            {messages.map((msg, msgIndex) => (
                <div
                    key={msg.id}
                    className={`flex gap-3 ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                >
                    {/* Bot Avatar is hidden on mobile screen to make more space */}
                    {/* {msg.sender === "bot" && (
                        <div className="w-8 h-8 rounded-full bg-[#13670B] flex-shrink-0 flex items-center justify-center">
                            {msg.isLoading ? (
                                <Loader size={20} className="text-white animate-spin" />
                            ) : msg.error ? (
                                <AlertCircle size={20} className="text-white" />
                            ) : (
                                <Bot size={20} className="text-white" />
                            )}
                        </div>
                    )} */}

                    {/* Message content */}
                    <div
                        className={`flex flex-col gap-3 ${msg.sender === "user"
                            ? isMobile ? "max-w-[85%]" : "max-w-lg"
                            : isMobile ? "max-w-[90%] w-full" : "max-w-4xl w-full"}`}
                    >
                        {/* Message bubble - only for user messages, bot messages are plain text */}
                        {msg.sender === "user" ? (
                            <div
                                className="p-3 rounded-xl bg-[#13670B] text-white break-words overflow-wrap-anywhere"
                            >
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm, remarkMath]}
                                    rehypePlugins={[rehypeKatex]}
                                    components={userMarkdownComponents}
                                >
                                    {sanitizeMarkdown(msg.text)}
                                </ReactMarkdown>
                            </div>
                        ) : (
                            <div
                                className={`${msg.error
                                    ? "text-[#7a1f1f] bg-red-50 border border-red-200 rounded-xl p-3"
                                    : `text-black break-words overflow-wrap-anywhere`}`}
                            >
                                {msg.isLoading ? (
                                    <LoadingProgress startTime={msg.timestamp} />
                                ) : (
                                    <>
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm, remarkMath]}
                                            rehypePlugins={[rehypeKatex]}
                                            components={botMarkdownComponents}
                                        >
                                            {sanitizeMarkdown(msg.text)}
                                        </ReactMarkdown>

                                        {msg.error && (
                                            <div className="mt-2 text-sm font-semibold bg-red-50 border border-red-200 rounded px-2 py-1">
                                                <p>Error: {msg.error}</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Display data if available */}
                        {msg.data && !msg.isLoading && !msg.error && (
                            <div className="w-full mt-2 md:mt-3">
                                <DataDisplay
                                    data={msg.data}
                                    summaryText={getShareContextText(messages, msgIndex)}
                                />
                            </div>
                        )}

                        {/* Feedback buttons for bot messages */}
                        {msg.sender === "bot" && !msg.isLoading && !msg.error && sessionId && (
                            <MessageFeedback
                                message={msg}
                                sessionId={sessionId}
                                interactionId={msg.interactionId}
                                onFeedbackChange={(feedback) => {
                                    onMessageUpdate?.(msg.id, { feedback });
                                }}
                                onChartClick={() => onChartRequest?.("Create chart")}
                            />
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ChatLog;

